import React, { useState, useRef, useEffect } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import moment from "moment";
import "react-time-picker/dist/TimePicker.css";
import "./EventStyles.css";
import calenderlogo from "../../../../assets/images/calenderlogo.svg";

import { useAuth } from "../../../../firebase/AuthProvider";
import {
  AllEvent,
  AllUsers,
  userTask,
} from "../../../../interfaces/identity/calendar/Events";
import {
  createEvent,
  getAllEmployees,
  getAllEvents,
  getAllUserTask,
} from "../../../../services/calendarService/EventService";
import { getTaskById, getTaskParticipant, getTasks } from "../../../../services/taskServices/TaskService";
import { getAllParticipants } from "../../../../services/participantServices/ParticipantService";
import { toast } from "react-toastify";
import Loader from "../../loader/Loader";
import SelectDropdown from "../../../ui/Dropdown/SelectDropdown";
import ParticipantSelectDropdown from "../../../ui/Dropdown/ParticipantSelectDropdown";
import { CALENDAR_VIEW } from "../../../../Data/SD";
import RecurrenceSelector from "./RecurrenceSelector"; // Import the new component
import { RRule } from "rrule"; // Import RRule
import CustomInput from "../CustomInput";
import AddPlace from "../../../../assets/images/Add-Place.svg";
import Title from "../../../../assets/images/AddEventTitle.png";
import AddDate from "../../../../assets/images/Add-Date.svg";
import AddTime from "../../../../assets/images/Add-Time.svg";
import AddTask from "../../../../assets/images/Add-Task.svg";
import colordot from "../../../../assets/images/color-dot.svg";
import AddNotes from "../../../../assets/images/AddNotes.svg";
import LinkIconMeet from "../../../../assets/images/LinkIconMeet.svg";
import Attendees from "../../../../assets/images/AddAttendees.svg";
import { FiChevronDown } from "react-icons/fi";
import CustomDropdown from "../CustomDropdown";
import TimePicker from "../TimePicker";
import TaskDropdown from "../TaskDropdown";
import Select, { MultiValue } from "react-select";
import AssignedParticipant from "../../../../interfaces/participant/AssignedParticipant";
import TaskFilters from "../../../../interfaces/task/TaskFilters";
import Tag from "../../../../interfaces/tag/Tag";
import { GetUserWhoNotHaveEmails } from "../../../../utilities/common/commonFuntion";

const ChevronDown = FiChevronDown as any;

interface EventData {
  title: string;
  startTime: string;
  endTime: string;
  description: string;
  location: string;
  meetingDetails: string;
  ownerUserID: string;
  participantUserIds: string[];
  externalUserEmails: string[];
  taskId: number;
  isSprout: boolean;
  recurrenceRule?: string; // Add recurrenceRule
  time_zone?: string; // Add time_zone
}

// Helper function to generate time options in 15-minute increments, formatted for 12-hour clock
const generateTimeOptions = () => {
  const times = [];
  let time = moment().startOf("day");
  const endOfDay = moment().endOf("day");

  while (time.isSameOrBefore(endOfDay)) {
    times.push(time.format("h:mm A"));
    time.add(15, "minutes");
  }
  return times;
};

const timeOptions = generateTimeOptions();

// Helper to get the current time rounded to the nearest 15 minutes
const getRoundedCurrentTime = () => {
  const now = moment();
  const roundedMinutes = Math.ceil(now.minute() / 15) * 15;
  return now.startOf("hour").add(roundedMinutes, "minutes");
};

interface AddEventProps {
  event?: AllEvent | null;
  clickedDateTime?: moment.Moment | null;
  onClose?: () => void;
  onEventCreated?: () => void;
  calanderView?: string;
  meetingTask?: any;
}

export default function AddEvent({
  meetingTask,
  event,
  clickedDateTime,
  onClose,
  onEventCreated,
  calanderView,
}: AddEventProps) {
  const { currentUser, currentOrganization } = useAuth();
  const userId = (currentUser as any)?.uid;
  const token = (currentUser as any)?.accessToken;

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [employees, setEmployees] = useState<AllUsers[]>([]);
  const [participantOptions, setParticipantOptions] = useState<AllUsers[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<AllEvent[]>([]);
  const [userTasks, setUserTasks] = useState<userTask[]>([]);
  const [isOnline, setIsOnline] = useState(true);
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Get current timezone
  const taskParticipantRef = useRef<string | null>(null);

  // New state for waiver participants and filtered tasks
  const [waiverParticipants, setWaiverParticipants] = useState<AssignedParticipant[]>([]);
  const [selectedWaiverParticipantIds, setSelectedWaiverParticipantIds] = useState<number[]>([]);

  // Initialize filteredTasks with meetingTask if provided, so dropdown can display it immediately
  const initialFilteredTasks: userTask[] = meetingTask?.id ? (() => {
    const taskIdNum = typeof meetingTask.id === 'string' ? Number(meetingTask.id) : meetingTask.id;
    return [{
      id: taskIdNum,
      title: meetingTask.title || "",
      description: meetingTask.description || "",
      assigneeRole: "",
      participants: meetingTask.participant?.id ? [meetingTask.participant.id.toString()] : [],
      tags: [],
      templateTasks: [],
    }];
  })() : [];

  const [filteredTasks, setFilteredTasks] = useState<userTask[]>(initialFilteredTasks);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskLoadError, setTaskLoadError] = useState<string>("");
  const [isWaiverParticipantMenuOpen, setIsWaiverParticipantMenuOpen] = useState(false);

  // New state for the selected date
  const [selectedDate, setSelectedDate] = useState<string>(
    event?.startTime
      ? moment(event.startTime).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD")
  );

  // Set default start and end times to the nearest 15-minute increment from now
  const defaultStartTime = getRoundedCurrentTime().format("h:mm A");
  const defaultEndTime = getRoundedCurrentTime()
    .add(30, "minutes")
    .format("h:mm A");

  // Initialize taskId if meetingTask is provided
  const initialTaskId = meetingTask?.id
    ? (typeof meetingTask.id === 'string' ? Number(meetingTask.id) : meetingTask.id)
    : 0;

  // Initialize participantUserIds - set participant ID from meetingTask if provided
  const initialParticipantUserIds: string[] = meetingTask?.participant?.id
    ? [String(meetingTask.participant.id)]
    : [];

  const [formData, setFormData] = useState<EventData>({
    title: "",
    startTime:
      event?.startTime && calanderView != CALENDAR_VIEW.month
        ? moment(event.startTime).format("h:mm A")
        : defaultStartTime,
    endTime:
      event?.startTime && calanderView != CALENDAR_VIEW.month
        ? moment(event.endTime).format("h:mm A")
        : defaultEndTime,
    description: "",
    location: "",
    meetingDetails: "",
    ownerUserID: userId,
    participantUserIds: initialParticipantUserIds,
    externalUserEmails: [],
    taskId: initialTaskId,
    isSprout: false,
    recurrenceRule: event?.recurrenceRule || undefined, // Initialize recurrenceRule
    time_zone: event?.time_zone || currentTimeZone, // Initialize time_zone
  });
  useEffect(() => {
    setParticipantOptions(employees);
  }, [employees]);

  const handleParticipantsChange = async (data: {
    participantUserIds: string[];
    externalUserEmails: string[];
  }) => {
    const taskParticipantRemoved =
      taskParticipantRef.current &&
      !data.participantUserIds.includes(taskParticipantRef.current);


    setFormData(prev => ({
      ...prev,
      participantUserIds: data.participantUserIds,
      externalUserEmails: data.externalUserEmails,
      taskId: taskParticipantRemoved ? 0 : prev.taskId, // ✅ SAFE
    }));

    if (taskParticipantRemoved) {
      setParticipantOptions(employees);
      setFilteredTasks(userTasks);
      loadTasksForParticipant(null);
    }
    else {
      const participantIds = data.participantUserIds.map(id => Number(id));
      await loadTasksForParticipant(participantIds);
    }
  };

  // const handleParticipantsChange = async (data: {
  //   participantUserIds: string[];
  //   externalUserEmails: string[];
  // }) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     participantUserIds: data.participantUserIds,
  //     externalUserEmails: data.externalUserEmails,
  //     taskId: 0, // ✅ clear task ONLY
  //   }));

  //   const participantIds = data.participantUserIds.map(id => Number(id));

  //   if (participantIds.length > 0) {
  //     await loadTasksForParticipant(participantIds);
  //   } else {
  //     await loadTasksForParticipant(null);
  //   }
  // };

  const handleRecurrenceChange = (rruleString?: string) => {
    setFormData((prev) => ({
      ...prev,
      recurrenceRule: rruleString,
    }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const updatedErrors = { ...prev };
      delete updatedErrors[name];
      return updatedErrors;
    });
  };

  // The time change handler for the new dropdown components
  const handleTimeChange = (name: string, value: string | number) => {
    // Ensure value is a string for time operations
    if (typeof value !== "string") return;

    setFormData((prev) => {
      const updatedFormData = { ...prev, [name]: value };

      // If the start time is changed, auto-fill the end time to 30 minutes later
      if (name === "startTime" && value) {
        const startTimeMoment = moment(value, "h:mm A");
        const endTimeMoment = startTimeMoment.add(30, "minutes");
        updatedFormData.endTime = endTimeMoment.format("h:mm A");
      }
      return updatedFormData;
    });
  };

  const handleTaskChange = async (value: string | number) => {
    const taskId = typeof value === "number" ? value : 0;
    setLoading(true);
    setTaskLoadError("");

    let participantIdToAdd: any = null;
    let newOptions = employees;

    if (taskId !== 0) {
      try {
        if (token) {
          // Call GET /api/task/participant?taskId={taskId} as per requirements
          const taskParticipant = await getTaskParticipant(taskId.toString(), token);

          if (taskParticipant) {
            // Check if the participant is deactivated
            if (taskParticipant.deactivated) {
              // Don't select deactivated participants
              participantIdToAdd = null;
              newOptions = employees;

              // Clear any existing waiver participant selections
              if (selectedWaiverParticipantIds.length > 0) {
                setSelectedWaiverParticipantIds([]);
              }

              // Show toast message explaining why participant wasn't selected
              toast.info("The participant for this task is deactivated and cannot be selected.");

              // Refresh task options
              await loadTasksForParticipant(null);
            } else {
              participantIdToAdd = taskParticipant.id;
              taskParticipantRef.current = String(participantIdToAdd);
              // Keep all employees and only the participant from the task.
              newOptions = employees.filter(
                (e) =>
                  (e as any).type !== "participant" ||
                  String((e as any).userId) === String(participantIdToAdd)
              );

              // Re-check waiver participant selections
              const taskParticipantId = taskParticipant.id;

              // Check if the task participant matches any waiver participant
              // Convert taskParticipantId to string for comparison since waiverParticipants[].id is string
              const matchingWaiverParticipant = waiverParticipants.find(
                (wp) => String(wp.id) === String(taskParticipantId)
              );

              if (matchingWaiverParticipant) {
                // If task participant matches a waiver participant, select it
                // Convert waiver participant id to number for selectedWaiverParticipantIds
                const waiverParticipantIdNum = Number(matchingWaiverParticipant.id);

                // Check if this waiver participant is already selected
                const isAlreadySelected = selectedWaiverParticipantIds.length === 1 &&
                  selectedWaiverParticipantIds[0] === waiverParticipantIdNum;

                if (!isAlreadySelected) {
                  // Only update selection and fetch tasks if not already selected
                  setSelectedWaiverParticipantIds([waiverParticipantIdNum]);
                  // Refresh task options since participant changed
                  await loadTasksForParticipant([waiverParticipantIdNum]);
                }
                // If already selected, skip API call - task list is already filtered correctly
              } else {
                // Task participant doesn't match any waiver participant
                if (selectedWaiverParticipantIds.length > 1) {
                  // If more than one waiver participant is selected, remove all
                  setSelectedWaiverParticipantIds([]);
                  toast.info("Multiple waiver participants selected. Cleared to match task participant.");
                  // Refresh task options since participant changed
                  await loadTasksForParticipant(null);
                } else if (selectedWaiverParticipantIds.length === 1) {
                  // If exactly one waiver participant is selected
                  const selectedId = selectedWaiverParticipantIds[0];
                  if (selectedId !== taskParticipantId) {
                    // Clear if it doesn't match
                    setSelectedWaiverParticipantIds([]);
                    toast.info("Waiver participant cleared - doesn't match task participant.");
                    // Refresh task options since participant changed
                    await loadTasksForParticipant(null);
                  }
                  // If it matches, skip API call - task list is already filtered correctly
                } else {
                  // No waiver participant selected, refresh with all tasks
                  await loadTasksForParticipant(null);
                }
              }
            }
          } else {
            // Task participant fetch failed (400/403/404)
            setTaskLoadError("Unable to load participant for selected task.");
            // Clear invalid selections
            if (selectedWaiverParticipantIds.length > 0) {
              setSelectedWaiverParticipantIds([]);
            }
          }
        } else {
          console.error("Auth token is not available.");
        }
      } catch (error) {
        console.error("Error fetching task participant:", error);
        setTaskLoadError("Unable to load participant for selected task.");
      }
    } else {
      // Task cleared - refresh task list based on current waiver participant selection
      if (selectedWaiverParticipantIds.length > 0) {
        await loadTasksForParticipant(selectedWaiverParticipantIds);
      } else {
        await loadTasksForParticipant(null);
      }
    }

    setParticipantOptions(newOptions);
    // setFormData((prev) => ({
    //   ...prev,
    //   taskId,
    //   participantUserIds: participantIdToAdd
    //     ? [String(participantIdToAdd)]
    //     : [],
    //   externalUserEmails: [],
    // }));

    // setFormData((prev) => ({
    //   ...prev,
    //   taskId,
    //   ...(participantIdToAdd
    //     ? { participantUserIds: [String(participantIdToAdd)] }
    //     : {}),
    // }));
    setFormData((prev) => {
      if (!participantIdToAdd) {
        return { ...prev, taskId };
      }

      const taskParticipantId = String(participantIdToAdd);

      const alreadyExists = prev.participantUserIds.includes(taskParticipantId);

      return {
        ...prev,
        taskId,
        participantUserIds: alreadyExists
          ? prev.participantUserIds // ✅ keep everything
          : [...prev.participantUserIds, taskParticipantId], // ✅ add safely
      };
    });

    setLoading(false);
  };

  async function fetchAllEmployee() {
    if (!currentUser) return;

    setLoading(true);
    try {
      const response = await getAllEmployees(token);
      if (response) {
        setEmployees(response);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchAllUserTasks() {
    if (!currentUser || !token) return;
    setLoading(true);

    try {
      const response = await getAllUserTask(token);
      if (response) {
        setUserTasks(response);

        // If meetingTask is provided, ensure it's included in filteredTasks
        if (meetingTask?.id) {
          const taskIdNum = typeof meetingTask.id === 'string' ? Number(meetingTask.id) : meetingTask.id;
          const taskExists = response.some(task => task.id === taskIdNum);
          if (!taskExists) {
            const meetingTaskAsUserTask: userTask = {
              id: taskIdNum,
              title: meetingTask.title || "",
              description: meetingTask.description || "",
              assigneeRole: "",
              participants: meetingTask.participant?.id ? [meetingTask.participant.id.toString()] : [],
              tags: [],
              templateTasks: [],
            };
            setFilteredTasks([...response, meetingTaskAsUserTask]);
          } else {
            setFilteredTasks(response);
          }
        } else {
          setFilteredTasks(response);
        }
      }
    } catch (error) {
      console.error("Error fetching user tasks:", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadTasksForParticipant(participantIds: number[] | null): Promise<userTask[]> {
    if (!currentUser || !token) return [];

    setLoadingTasks(true);
    setTaskLoadError("");

    try {
      if (participantIds === null || participantIds.length === 0) {
        setFilteredTasks(userTasks);
        setLoadingTasks(false);
        return userTasks;
      }

      const filters: TaskFilters = {
        participants: participantIds.map(id => id.toString()),
      };

      const result = await getTasks(
        token,
        undefined,
        1,
        100,
        filters,
        "",
        false,
        false,
        false,
        undefined
      );

      if (result && result.tasks) {
        const mappedTasks: userTask[] = result.tasks.map((task) => ({
          id: Number(task.id),
          title: task.title,
          description: task.description || "",
          assigneeRole: task.assigneeRoleName || "",
          participants: task.participant ? [task.participant.id.toString()] : [],
          tags: task.tags ? task.tags.map((tag: Tag) => tag.name) : [],
          templateTasks: [],
          friendlyId : Number(task.friendlyId)
        }));
        setFilteredTasks(mappedTasks);
        setLoadingTasks(false);
        return mappedTasks;
      } else {
        setFilteredTasks([]);
        setLoadingTasks(false);
        return [];
      }
    } catch (error) {
      console.error("Error loading tasks for participant:", error);
      setTaskLoadError("Unable to load tasks for selected participant.");
      setFilteredTasks([]);
      setLoadingTasks(false);
      return [];
    }
  }

  async function fetchWaiverParticipants() {
    if (!currentUser || !token) return;

    setLoading(true);
    try {
      const response = await getAllParticipants(token, false);
      if (response) {
        setWaiverParticipants(response);
      }
    } catch (error) {
      console.error("Error fetching waiver participants:", error);
    } finally {
      setLoading(false);
    }
  }

  const handleWaiverParticipantChange = async (selectedOptions: MultiValue<{ value: string; label: string }>) => {
    const selectedIds = selectedOptions ? selectedOptions.map(opt => Number(opt.value)) : [];
    setSelectedWaiverParticipantIds(selectedIds);

    const newFilteredTasks = await loadTasksForParticipant(selectedIds.length > 0 ? selectedIds : null);

    if (formData.taskId !== 0) {
      const currentTaskInList = newFilteredTasks.some(task => task.id === formData.taskId);
      if (!currentTaskInList) {
        setFormData(prev => ({ ...prev, taskId: 0 }));
      }
    }
  };

  async function fetchAllEvents() {
    if (!currentUser) return;

    setLoading(true);
    try {
      // Fetch events for a wide range to ensure we get all necessary data for now
      const start = moment().subtract(1, "year").toDate();
      const end = moment().add(1, "year").toDate();
      const response = await getAllEvents(token, start, end);
      if (response) {
        setCalendarEvents(response);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    if (!formData.startTime) {
      newErrors.startTime = "Start time is required";
    }
    if (!formData.endTime) {
      newErrors.endTime = "End time is required";
    }

    // Validate that meeting is not in the past
    if (selectedDate && formData.startTime) {
      const meetingDateTime = moment(`${selectedDate} ${formData.startTime}`, "YYYY-MM-DD h:mm A");
      const now = moment();

      if (meetingDateTime.isBefore(now)) {
        newErrors.startTime = "Cannot create meetings in the past";
      }
    }

    const usersWithoutEmail = GetUserWhoNotHaveEmails(
      participantOptions,
      formData.participantUserIds
    );

    if (usersWithoutEmail.length > 0) {
      newErrors.participantUserIds = "SomeParticipantNoEmail";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };




  // const validateForm = () => {
  //   const newErrors: Record<string, string> = {};

  //   if (!formData.title.trim()) {
  //     newErrors.title = "Title is required";
  //   }

  //   if (!formData.startTime) {
  //     newErrors.startTime = "Start time is required";
  //   }

  //   if (!formData.endTime) {
  //     newErrors.endTime = "End time is required";
  //   }

  //   const hasParticipants =
  //     formData.participantUserIds.length > 0 ||
  //     formData.externalUserEmails.length > 0;

  //   const hasTask = formData.taskId && formData.taskId !== 0;

  //   // 🔐 WAIV-1315 RULE
  //   if (hasParticipants && !hasTask) {
  //     newErrors.taskId = "Task is required when participant is selected";
  //   }

  //   if (hasTask && !hasParticipants) {
  //     newErrors.participantUserIds = "Participant is required when task is selected";
  //   }

  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    if (!token) {
      console.error("Missing auth token.");
      return;
    }

    // Parse the 12-hour time and format it as 24-hour time
    const startTime24hr = moment(formData.startTime, "h:mm A").format("HH:mm");
    const endTime24hr = moment(formData.endTime, "h:mm A").format("HH:mm");

    // Merge the selectedDate with the 24-hour time in the specified timezone
    const timezone = formData.time_zone || currentTimeZone;

    // Calculate start and end moments in the specified timezone
    const startMoment = moment.tz(`${selectedDate} ${startTime24hr}`, timezone);
    let endMoment = moment.tz(`${selectedDate} ${endTime24hr}`, timezone);

    // If end time is less than or equal to start time, add 1 day to endMoment
    if (endMoment.isSameOrBefore(startMoment)) {
      endMoment.add(1, "day");
    }

    // Send the exact date/time as picked by frontend in ISO format
    // Backend expects: 2026-01-16T20:45:00.000Z format
    // Format as ISO string with milliseconds - backend will handle adding 'Z' if needed
    // The time_zone field tells backend the original timezone
    // Backend will return the same date/time, and frontend will convert to local for display
    const eventDataToSend = {
      ...formData,
      startTime: startMoment.format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
      endTime: endMoment.format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
      online: isOnline,
      time_zone: timezone,
      recurrenceRule: formData.recurrenceRule,
      location: formData.isSprout ? formData.location : "Waivera",
    };
    setLoading(true);
    try {
      const result = await createEvent(eventDataToSend, token);

      if (result.success) {
        toast.success(result.message);
        fetchAllEvents();
        if (onClose) onClose();
        if (onEventCreated) onEventCreated();
      } else if (result.statusCode === 400) {
        toast.warning(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error("Error submitting event:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllEmployee();
    fetchAllEvents();
    fetchAllUserTasks();
    fetchWaiverParticipants();
  }, []);

  // Auto-select task and set participant ID when meetingTask is provided from TaskPage
  useEffect(() => {
    if (meetingTask?.id) {
      // Convert task.id (string) to number for userTask
      const taskIdNum = typeof meetingTask.id === 'string' ? Number(meetingTask.id) : meetingTask.id;

      // Ensure the task is in the filtered tasks list
      setFilteredTasks(prev => {
        const taskExists = prev.some(task => task.id === taskIdNum);
        if (!taskExists) {
          // Add the meeting task to filtered tasks if it doesn't exist
          const meetingTaskAsUserTask: userTask = {
            id: taskIdNum,
            title: meetingTask.title || "",
            description: meetingTask.description || "",
            assigneeRole: "",
            participants: meetingTask.participant?.id ? [meetingTask.participant.id.toString()] : [],
            tags: [],
            templateTasks: [],
          };
          return [...prev, meetingTaskAsUserTask];
        }
        return prev;
      });

      // Simply set the participant ID from meetingTask
      if (meetingTask.participant?.id) {
        const participantId = String(meetingTask.participant.id);
        setFormData(prev => ({
          ...prev,
          taskId: taskIdNum,
          participantUserIds: [participantId],
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingTask]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape" && onClose) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);

    return () => {
      window.removeEventListener("keydown", handleEsc);
    };
  }, [onClose]);

  const timeObjectOptions = timeOptions.map((time) => ({
    label: time,
    value: time,
  }));
  // const taskObjectOptions = userTasks.map((task) => ({
  //   label: `[${currentOrganization?.abbreviation}-${task.id}] ${task.title}`,
  //   value: task.id,
  // }));

  // Use filteredTasks instead of userTasks for task options
  const taskObjectOptions = filteredTasks.map((task) => ({
    label: task.title,
    value: task.id,
    taskId: `${task.id}`,
    participant: task.participants?.[0] ?? "N/A",
    friendlyId : task.friendlyId
  }));

  // Options for waiver participant dropdown
  const waiverParticipantOptions = waiverParticipants.map((participant) => ({
    value: participant.id.toString(),
    label: `${participant.firstName} ${participant.lastName}`,
  }));

  const selectedWaiverParticipantOptions = waiverParticipantOptions.filter(opt =>
    selectedWaiverParticipantIds.includes(Number(opt.value))
  );

  const formattedDisplayDate = moment(selectedDate).format(
    "dddd, MMMM DD, YYYY"
  );

  const hasParticipants =
    formData.participantUserIds.length > 0 ||
    formData.externalUserEmails.length > 0;

  const selectedParticipantLabels = participantOptions
    .filter((user) => formData.participantUserIds.includes(user.userId))
    .map((user) => user.userName);

  const participantLabelParts = [
    ...selectedParticipantLabels,
    ...formData.externalUserEmails,
  ];

  useEffect(() => {
    if (!clickedDateTime) return;

    setSelectedDate(clickedDateTime.format("YYYY-MM-DD"));

    setFormData((prev) => ({
      ...prev,
      startTime: clickedDateTime.format("h:mm A"),
      endTime: clickedDateTime.clone().add(30, "minutes").format("h:mm A"),
    }));
  }, [clickedDateTime]);

  useEffect(() => {
    if (!formData.isSprout) {
      // Waivera Meeting
      setFormData((prev) => ({
        ...prev,
        location: "Waivera",
      }));
    } else {
      // External Meeting
      setFormData((prev) => ({
        ...prev,
        location: "",
      }));
    }
  }, [formData.isSprout]);


  return (
    <>
      {loading && <Loader />}

      <div className="">
        <div className="top_bar_modal top_bar_modal_flx flex-wrap px-3 pt-4 pb-0 !border-none !mb-2" style={{ justifyContent: "end" }}>
          {/* <div className="top_bar_modal_head">
            <h2>Create New Meeting</h2>
            <p>{formattedDisplayDate}</p>
          </div> */}
          {/* <p className="!text-lg text-[#747474] font-medium m-0">Event Title</p> */}

          {/* <div>
            <input
              type="text"
              name="title"
              className="!text-lg text-[#747474] font-medium m-0"
              placeholder="Add Event Title"
              value={formData.title}
              onChange={handleChange}
              style={{
                border: "none",
                background: "transparent",
                outline: "none",
                boxShadow: "none",
                color: "#747474",
              }}
            />

            {errors.title && (
              <p className="text-red-500 text-sm mt-1">{errors.title}</p>
            )}
          </div> */}


          {/* <img
            src={colordot}
            alt="color-dot"
            className="w-[24px] h-[24px]"
          /> */}
          {/* <div className="sprout-toggle-wrapper">
            <button
              type="button"
              onClick={() =>
                setFormData((prev) => ({ ...prev, isSprout: !prev.isSprout }))
              }
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: formData.isSprout ? "#776ce6" : "#fff",
                color: formData.isSprout ? "#fff" : "#000",
                border: `1px solid ${formData.isSprout ? "#776ce6" : "#ccc"}`,
                borderRadius: "4px",
                padding: "0.5rem 1rem",
                cursor: "pointer",
              }}
            >
              <img
                src={calenderlogo}
                alt="Sprout Logo"
                style={{ height: 20, marginRight: 4 }}
              />
              Meeting
            </button>
          </div> */}
        </div>
        <div className="flex justify-center gap-3 mb-3.5">
          <button
            type="button"
            onClick={() =>
              setFormData((prev) => ({ ...prev, isSprout: false }))
            }
            className={`px-4 py-1.5 text-base font-normal rounded-md transition-all
      ${formData.isSprout
                ? " text-[#747474] hover:text-gray-700"
                : "bg-[#F4F4F4] text-[#7E57C2] font-normal"
              }
    `}
          >
            Waivera Meeting
          </button>
          <button
            type="button"
            onClick={() => setFormData((prev) => ({ ...prev, isSprout: true }))}
            className={`px-4 py-1.5 text-base font-normal rounded-md transition-all
      ${!formData.isSprout
                ? " text-[#747474] hover:text-gray-700"
                : "bg-[#F4F4F4] text-[#7E57C2] font-normal"
              }
    `}
          >
            External Meeting
          </button>

        </div>
        <form onSubmit={handleSubmit}>
          <div>

            {/* {!formData.isSprout ? (
              <>
                <div className="col-md-12">
                  <div className="custom_input_flled">
                    <label>Location</label>
                    <input
                      type="text"
                      name="location"
                      placeholder="Location"
                      value={formData.location}
                      onChange={handleChange}
                      style={{ width: "100%" }}
                    />
                  </div>
                </div>
                <CustomInput
                  label="Title"
                  name="location"
                  placeholder="Location"
                  value={formData.location}
                  onChange={handleChange}
                  error={errors.location}
                  icon={AddPlace} // Optional: You can pass an icon image URL
                />
              </>
            ) : (
              <>
                <div className="col-md-12 border-t-2 border-[#F5F6F7] px-2.5 !h-[48px] flex">
                  <img src={LinkIconMeet} alt="LinkIconMeet" className="w-[30px] h-[30px]" />
                  <label className="!text-[#747474] !text-[13px] 2xl:!text-[16px] !font-normal m-0 ps-3">
                    Link
                  </label>
                  {formData.isSprout ? (
                    <div>
                      <p className="text-muted italic text-xs w-full truncate ps-3 m-0">
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="col-md-12">
                        <div className="custom_input_flled">
                          <input
                            type="text"
                            name="meetingDetails"
                            placeholder={
                              formData.isSprout
                                ? "Sprout link AI"
                                : "User Place Link"
                            }
                            value={formData.meetingDetails}
                            onChange={handleChange}
                            style={{ width: "100%" }}
                          />
                          {errors.meetingDetails && (
                            <p className="text-red-500 text-sm mt-1">
                              {errors.meetingDetails}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </>
            )} */}

            <CustomInput
              label="Add Event Title"
              name="title"
              placeholder="Add Event Title"
              value={formData.title}
              onChange={handleChange}
              error={errors.title}
              icon={Title}
            />

            <CustomInput
              label="Location"
              name="location"
              placeholder={formData.isSprout ? "Location" : "Waivera"}
              value={formData.location}
              onChange={handleChange}
              error={errors.location}
              icon={formData.isSprout ? AddPlace : LinkIconMeet}
              disabled={!formData.isSprout}
            />

            {/* <CustomInput
              label="Title"
              name="title"
              placeholder="Enter Title"
              value={formData.title}
              onChange={handleChange}
              error={errors.title}
              icon={AddPlace} // Optional: You can pass an icon image URL
            /> */}

            <div className="grid grid-cols-2">
              <CustomInput
                label="Meeting Date"
                name="meetingDate"
                type="date"
                value={selectedDate}
                min={moment().format("YYYY-MM-DD")}
                onChange={(e) => setSelectedDate(e.target.value)}
                icon={AddDate}
              />
              <TimePicker
                label="Start"
                name="startTime"
                value={formData.startTime}
                onChange={(value) => handleTimeChange("startTime", value)}
                // options={timeObjectOptions}
                error={errors.startTime}
                icon={AddTime}
              />
              {/* <CustomDropdown
                label="End Time"
                name="endTime"
                value={formData.endTime}
                onChange={(value) => handleTimeChange("endTime", value)}
                options={timeObjectOptions}
                error={errors.endTime}
                icon={AddTime}
              /> */}
            </div>
            <div className="grid grid-cols-2">
              <RecurrenceSelector
                value={formData.recurrenceRule}
                onChange={handleRecurrenceChange}
                startTime={moment(
                  `${selectedDate} ${formData.startTime}`,
                  "YYYY-MM-DD h:mm A"
                ).toISOString()}
                timeZone={formData.time_zone || currentTimeZone}
              />
              <TimePicker
                label="End"
                name="endTime"
                value={formData.endTime}
                onChange={(value) => handleTimeChange("endTime", value)}
                // options={timeObjectOptions}
                error={errors.endTime}
                icon={AddTime}
              />
            </div>


            <ParticipantSelectDropdown
              label="Participants"
              name="participants"
              participantUserIds={formData.participantUserIds}
              externalUserEmails={formData.externalUserEmails}
              onChange={handleParticipantsChange}
              options={participantOptions}
              error={errors.participantUserIds}
            />

            {/* <div className="col-md-12">
              <div className="relative w-full">
                <div
                  className="h-[48px] border-x-[1px] border-t-2 border-[#F5F6F7] flex items-center relative"
                >
                  <img
                    src={Attendees}
                    alt="Attendees"
                    className="absolute w-[30px] h-[30px] pointer-events-none z-10 left-2"
                  />
                  <Select
                    isMulti
                    value={selectedWaiverParticipantOptions}
                    onChange={handleWaiverParticipantChange}
                    onMenuOpen={() => setIsWaiverParticipantMenuOpen(true)}
                    onMenuClose={() => setIsWaiverParticipantMenuOpen(false)}
                    options={waiverParticipantOptions}
                    classNamePrefix="react-select"
                    placeholder="Select waiver participant(s)..."
                    isClearable
                    styles={{
                      container: (base) => ({
                        ...base,
                        width: "100%",
                      }),
                      control: (base, state) => ({
                        ...base,
                        border: "none",
                        boxShadow: "none",
                        backgroundColor: "transparent",
                        minHeight: "auto",
                        height: "100%",
                        cursor: "pointer",
                        paddingLeft: "2.5rem", // Space for icon
                        paddingRight: "2rem", // Space for arrow

                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: 0,
                      }),
                      menu: (base) => ({
                        ...base,
                        maxHeight: "150px", // Reduced height
                        zIndex: 9999,
                      }),
                      menuList: (base) => ({
                        ...base,
                        maxHeight: "150px", // Reduced height
                        padding: 0,
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        display: "none", // Hide default indicator
                      }),
                    }}
                    className="waiver-participant-select"
                  />
                  <ChevronDown
                    className={`absolute right-3 transition-transform duration-200 pointer-events-none ${isWaiverParticipantMenuOpen ? "rotate-180" : ""
                      }`}
                    size={18}
                    color="#777"
                  />
                </div>
                {taskLoadError && (
                  <p className="text-red-500 text-sm mt-1">{taskLoadError}</p>
                )}
              </div>
            </div > */}

            <TaskDropdown
              label="Add Task"
              name="taskId"
              value={formData.taskId || 0}
              onChange={handleTaskChange}
              options={taskObjectOptions}
              error={errors.taskId}
              icon={AddTask}
              loadingTasks={loadingTasks}
              disabled={!!meetingTask}
            />
            {!meetingTask && hasParticipants && formData.taskId === 0 && (
              <div className="mt-1 mb-1 pl-4 pr-4 text-xs text-warning">
                {participantLabelParts.map((label, index) => (
                  <span key={`${label}-${index}`} className="capitalize font-medium">
                    {label}
                    {index === participantLabelParts.length - 2 && " and "}
                    {index < participantLabelParts.length - 2 && ", "}
                  </span>
                ))}
                <span className="font-medium">
                  {" "}participant{participantLabelParts.length > 1 ? "s" : ""} added — please attach a task before submitting the request.
                </span>
              </div>
            )}
            {/* {
              loadingTasks && (
                <p className="text-sm text-gray-500 px-4">Loading tasks...</p>
              )
            } */}
          </div >
          {/* 
          <div className="custom_input_flled mb-0">
            <label>Description</label>
            <textarea
              name="description"
              placeholder="Description"
              value={formData.description}
              onChange={handleChange}
            />
          </div> */}
          < CustomInput
            name="description"
            placeholder="Add Notes"
            value={formData.description}
            onChange={handleChange}
            type="textarea"
            borderBotttom
            icon={AddNotes}
          />
          <div className="flex justify-end mt-4 gap-2 px-7 pb-7">
            <Button variant="secondary" onClick={onClose} className="!w-[150px]">
              Cancel
            </Button>
            <Button variant="primary" type="submit" className="!w-[150px]">
              {hasParticipants ? "Submit Request" : "Create"}
            </Button>
          </div>
        </form >
      </div >
    </>
  );
}
